import authCookie from './auth-cookie';
import appInsights, { SeverityLevel } from '../appInsights';

const customHeaders = {
  Authorization: `Bearer ${authCookie.get()}`,
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

async function successResult(response) {
  const text = await response.text();
  const data = text.trim().length > 0 ? JSON.parse(text) : {};

  return {
    data,
    success: true,
  };
}

async function callFetch(method, url, data, parseResponse = successResult) {
  const parameters = {
    method,
    headers: customHeaders,
  };

  /* eslint no-underscore-dangle: ["error", { "allow": ["__RUNTIME_CONFIG__"] }] */
  const parsedUrl = url.startsWith('http')
    ? url
    : window.__RUNTIME_CONFIG__.REACT_APP_ACCOUNT_API_URL + url;

  if (typeof data !== 'undefined') {
    parameters.body = JSON.stringify(data);
  }

  const response = await fetch(parsedUrl, parameters);

  if (!response.ok) {
    appInsights.trackException(
      `Failed fetch of the ${method}: ${parsedUrl} with error ${response.statusText} and status ${response.status}`,
      SeverityLevel.Error
    );
  }

  if (!response || !response.ok) {
    return {
      data: null,
      success: false,
      message: response
        ? response.statusText
        : `Error fetching data from ${parsedUrl}.`,
      statusCode: response ? response.status : 500,
    };
  }

  return parseResponse(response);
}

async function blobResult(response) {
  const data = await response;
  const headerValue = response.headers.get('content-disposition');
  const warning = response.headers.get('warning');
  const fileName = headerValue
    .split('filename=')[1]
    .split(';')[0]
    .replace(/"/g, '');
  return {
    data: await data.blob(),
    fileName,
    success: true,
    warning,
  };
}

async function get(url, enqueueSnackbar, t) {
  const result = await callFetch('GET', url);

  if (!result.success || result.data == null) {
    if (enqueueSnackbar && t) {
      enqueueSnackbar(t('global.messages.failedToRetrieveData'), {
        variant: 'error',
        persist: true,
      });
    }
  }

  return result.data;
}

async function post(
  url,
  data,
  enqueueSnackbar,
  t,
  parseResponse = successResult
) {
  const result = await callFetch('POST', url, data, parseResponse);

  if (result.success) {
    if (enqueueSnackbar && t) {
      enqueueSnackbar(t('global.messages.dataSaved'), {
        variant: 'success',
      });
    }
  } else if (enqueueSnackbar && t) {
    enqueueSnackbar(t('global.messages.dataSaveHasFailed'), {
      variant: 'error',
      persist: true,
    });
  }

  return result;
}

async function put(url, data, enqueueSnackbar, t) {
  const result = await callFetch('PUT', url, data, successResult);

  if (result.success) {
    if (enqueueSnackbar && t) {
      enqueueSnackbar(t('global.messages.dataSaved'), { variant: 'success' });
    }
  } else if (enqueueSnackbar && t) {
    enqueueSnackbar(t('global.messages.dataSaveHasFailed'), {
      variant: 'error',
      persist: true,
    });
  }

  return result;
}

async function downloadFile(url, data, enqueueSnackbar, closeSnackbar, t) {
  appInsights.trackEvent(`Download file from ${url}`);
  closeSnackbar();

  post(url, data, undefined, undefined, blobResult).then(result => {
    if (result.success) {
      const objectUrl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.display = 'none';
      link.href = objectUrl;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      appInsights.trackEvent(`Downloaded file successfully from ${objectUrl}`);
      if (
        result.warning !== undefined &&
        result.warning !== null &&
        enqueueSnackbar &&
        t
      ) {
        const warning = result.warning.split(';');
        if (warning[0] === '299') {
          const list = warning[1].replace(/,/g, ', ');
          const warningInfo = t('fileGenerationPage.missingTemplates') + list;
          enqueueSnackbar(warningInfo, {
            variant: 'warning',
            persist: true,
          });
        }
      }
    } else {
      if (enqueueSnackbar && t) {
        enqueueSnackbar(t('fileGenerationPage.downloadFileFailed'), {
          variant: 'error',
        });
      }
      appInsights.trackEvent(
        `Download file failed from ${url} with status code ${result.statusCode}`
      );
    }
  });
}

const api = {
  get,
  post,
  put,
  downloadFile,
};

export default api;
